import React, { useMemo } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import DataTable from 'src/shared/components/data-table/DataTable';
import type { DataTableColumns, DataTableState, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import type { StacCollection } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import styles from './CollectionListTable.module.scss';

type CollectionListTableProps = {
    collections: StacCollection[];
    onRowClick: (collection: StacCollection) => void;
};

const tableColumns: DataTableColumns = [
    { columnId: 'id', title: 'Id', isUnique: true },
    { columnId: 'title', title: 'Dataset', isSortable: true, width: '260px' },
    { columnId: 'identifier', title: 'Identifier', isSortable: true, width: '260px' },
    { columnId: 'description', title: 'Description', width: '260px' },
    { columnId: 'licence', title: 'Licence', width: '140px' },
    { columnId: 'covers', title: 'Covers', width: '220px' },
];

/** Rendered in UTC. These instants describe the data's coverage, so they must read the
 *  same for every viewer rather than shifting with the browser's timezone. */
const formatInstant = (iso: string): string => moment.utc(iso).format('D MMM YYYY');

/**
 * How long a dataset says it covers.
 *
 * STAC's first temporal interval is the overall one, and an open end is `null` — the
 * dataset is still being added to. That is a different thing from stating no extent at
 * all, so the two read differently rather than both becoming a blank cell.
 */
export const temporalCoverageOf = (collection: StacCollection): string => {
    const [overall] = collection.extent?.temporal?.interval ?? [];
    if (!overall) {
        return 'Not stated';
    }

    const [start, end] = overall;
    return `${start ? formatInstant(start) : 'Not stated'} → ${end ? formatInstant(end) : 'ongoing'}`;
};

const CollectionListTable = ({ collections, onRowClick }: CollectionListTableProps) => {
    const tableState: Partial<DataTableState> = useMemo(() => {
        const data = collections.map(collection => [
            collection.id,
            // A link, not just a clickable row: it can be opened in a new tab, reached by
            // keyboard, and read as a destination by a screen reader.
            <Link
                key='title'
                to={`/admin/catalogue/${collection.id}`}
                className='font-medium text-primary hover:underline'
                onClick={event => event.stopPropagation()}
            >
                {collection.title || collection.id}
            </Link>,
            <code key='identifier' className='text-xs bg-grey-10 px-2 py-1 rounded'>
                {collection.id}
            </code>,
            collection.description,
            collection.license,
            temporalCoverageOf(collection),
        ]);
        return { data, hiddenColumnIds: new Set(['id']) };
    }, [collections]);

    const handleRowSelect = (rowData: TableRowData) => {
        const id = rowData[0] as string;
        const collection = collections.find(candidate => candidate.id === id);
        if (collection) onRowClick(collection);
    };

    const emptyStateMessage = (
        <div className='flex flex-col items-center py-16 gap-2 text-center' data-testid='stac-collection-list-empty-state'>
            <h3 className='text-lg font-semibold text-primary'>No datasets catalogued yet</h3>
            <p className='text-sm text-grey-70'>Use &lsquo;New dataset&rsquo; to establish one.</p>
        </div>
    );

    return (
        <div className={styles.collectionList}>
            <DataTable columns={tableColumns} state={tableState} emptyStateMessage={emptyStateMessage} onRowSelect={handleRowSelect} />
        </div>
    );
};

export default CollectionListTable;
